import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import CommunityQA from '@/pages/CommunityQA';
import { GardenStream, CommunityTeaser, GrowerProfileModal } from '@/components/GardenStream';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { HomeSwapCarousel } from '@/components/HomeSwapCarousel';
import { MediaGallery, MediaPicker } from '@/components/CommunityMedia';
import Marketplace from '@/components/Marketplace';
import {
  Users, MapPin, Trophy, Sprout, Heart, MessageCircle, MessageCircleQuestion, Sparkles, Lock,
  Hand, UserCheck, Repeat, MoreHorizontal, Flag, Ban, Send, Award, Mail, Star, ArrowLeftRight, Pencil, Trash2, Image as ImageIcon, Leaf,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HUB_IMG = {
  market: 'https://images.unsplash.com/photo-1752051665095-be37be749e55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjb2xvcmZ1bCUyMHZlZ2V0YWJsZSUyMHNlZWQlMjBwYWNrZXRzfGVufDB8fHx8MTc4ODY3OTk0NXww&ixlib=rb-4.1.0&q=85',
  leaderboards: 'https://images.unsplash.com/photo-1629398781739-9caec1eb9409?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxnYXJkZW5lciUyMGhvbGRpbmclMjBoYXJ2ZXN0JTIwYmFza2V0JTIwdmVnZXRhYmxlc3xlbnwwfHx8fDE3ODg2Nzk5NDR8MA&ixlib=rb-4.1.0&q=85',
  qa: 'https://images.unsplash.com/photo-1788538812851-9aacd4189fa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBnYXJkZW5pbmclMjB0b2dldGhlciUyMGNvbW11bml0eXxlbnwwfHx8fDE3ODg2Nzk5NDR8MA&ixlib=rb-4.1.0&q=85',
};

const STYLE_COLORS = {
  organic: 'bg-[#6B9F4A]/15 text-[#4E7A33]',
  regenerative: 'bg-[#1A4331]/10 text-[#1A4331]',
  conventional: 'bg-[#8FB4D6]/18 text-[#5A7E9E]',
  mixed: 'bg-[#C9A85B]/18 text-[#8A6D2B]',
};

function Community({ user }) {
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [nearYou, setNearYou] = useState(null);
  const [boardsList, setBoardsList] = useState([]);
  const [activeBoard, setActiveBoard] = useState('weekly_harvest_value');
  const [board, setBoard] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('feed');
  const [shareOpen, setShareOpen] = useState(false);
  const [openProfile, setOpenProfile] = useState(null);
  const [pops, setPops] = useState({});
  const [regions, setRegions] = useState({ countries: [] });
  const [country, setCountry] = useState('global');
  const [regionCode, setRegionCode] = useState('');
  const [followingOnly, setFollowingOnly] = useState(false);
  const [unread, setUnread] = useState(0);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const [focusListing, setFocusListing] = useState(null);
  const openListing = (id) => { setFocusListing(id); setSection('market'); };

  const isPremium = user?.subscription_active && (user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro');
  const goUpgrade = () => navigate('/subscription');
  const myId = user?.user_id;

  const feedParams = useCallback(() => {
    const p = {};
    if (followingOnly) p.following = true;
    else if (regionCode) p.region = regionCode;
    else if (country !== 'global') p.country = country;
    return p;
  }, [followingOnly, regionCode, country]);

  const loadFeed = useCallback(async () => {
    try {
      const f = await axios.get(`${API}/community/feed`, { params: feedParams(), withCredentials: true });
      setFeed(f.data);
    } catch (e) { /* best effort */ }
  }, [feedParams]);

  const loadAll = useCallback(async () => {
    try {
      const [n, lb, c, mc, d, rg] = await Promise.all([
        axios.get(`${API}/community/people-near-you`, { withCredentials: true }),
        axios.get(`${API}/community/leaderboards`, { withCredentials: true }),
        axios.get(`${API}/community/challenges`, { withCredentials: true }),
        axios.get(`${API}/community/my-challenges`, { withCredentials: true }),
        axios.get(`${API}/community/digest`, { withCredentials: true }),
        axios.get(`${API}/community/regions`, { withCredentials: true }),
      ]);
      setNearYou(n.data);
      setBoardsList(lb.data.available || []);
      setChallenges(c.data);
      setMyChallenges(mc.data);
      setDigest(d.data);
      setRegions(rg.data || { countries: [] });
    } catch (e) { /* best effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadFeed(); }, [loadFeed]);
  useEffect(() => { axios.get(`${API}/community/qa/unseen`, { withCredentials: true }).then((r) => setUnseen(r.data?.count || 0)).catch(() => {}); }, [section]);
  useEffect(() => {
    if (!isPremium) return;
    const refresh = () => axios.get(`${API}/messages/unread-count`, { withCredentials: true }).then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [isPremium]);

  useEffect(() => {
    if (!activeBoard) return;
    axios.get(`${API}/community/leaderboards/${activeBoard}`, { withCredentials: true }).then((r) => setBoard(r.data)).catch(() => {});
  }, [activeBoard]);

  const joinChallenge = async (id) => {
    await axios.post(`${API}/community/challenges/${id}/join`, {}, { withCredentials: true }).catch(() => {});
    const mc = await axios.get(`${API}/community/my-challenges`, { withCredentials: true }).catch(() => ({ data: [] }));
    setMyChallenges(mc.data);
  };
  const toggleHelpful = async (postId) => {
    const r = await axios.post(`${API}/community/posts/${postId}/helpful`, {}, { withCredentials: true }).catch(() => null);
    if (r) setFeed((f) => f.map((p) => p.id === postId ? { ...p, viewer_helpful: r.data.helpful, helpful_count: r.data.helpful_count } : p));
  };
  const nudgePost = async (postId) => {
    const r = await axios.post(`${API}/community/posts/${postId}/nudge`, {}, { withCredentials: true }).catch(() => null);
    if (r) setFeed((f) => f.map((p) => p.id === postId ? { ...p, viewer_nudged: r.data.nudged, nudge_count: r.data.nudge_count } : p));
  };
  const pickCountry = (c) => { setFollowingOnly(false); setCountry(c); setRegionCode(''); };
  const activeCountry = regions.countries?.find((c) => c.code === country);
  const doubleLike = (post) => {
    setPops((prev) => ({ ...prev, [post.id]: (prev[post.id] || 0) + 1 }));
    if (!post.viewer_helpful) toggleHelpful(post.id);
  };
  const removeAuthorPosts = (authorId) => setFeed((f) => f.filter((p) => p.author_id !== authorId));
  const editPost = async (postId, note) => {
    await axios.patch(`${API}/community/posts/${postId}`, { note }, { withCredentials: true }).catch(() => {});
    setFeed((f) => f.map((p) => p.id === postId ? { ...p, note } : p));
  };
  const deletePost = async (postId) => {
    await axios.delete(`${API}/community/posts/${postId}`, { withCredentials: true }).catch(() => {});
    setFeed((f) => f.filter((p) => p.id !== postId));
  };

  const ACTIONS = [
    { id: 'feed', label: 'Feed', icon: Sprout, section: 'feed' },
    { id: 'profile', label: 'My Profile', icon: Users, kind: 'profile' },
    { id: 'ask', label: 'Ask advice', icon: MessageCircleQuestion, section: 'qa' },
    { id: 'market', label: 'Vege Exchange', icon: ArrowLeftRight, section: 'market' },
    { id: 'leaderboards', label: 'Leaderboards', icon: Trophy, section: 'leaderboards' },
    { id: 'challenges', label: 'Challenges', icon: Star, section: 'challenges' },
  ].filter((a) => a.id !== 'challenges' || user?.challenges_enabled !== false);

  const pickAction = (a) => {
    if (a.kind === 'profile') { isPremium ? setOpenProfile(myId) : goUpgrade(); return; }
    setSection(a.section);
  };

  const feedFilterKey = `${followingOnly}-${country}-${regionCode}`;

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Compact header — mirrors native AppHeader */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[13px] text-[#7A6B57] font-medium">People growing near you</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Community</h1>
          </div>
          {isPremium && (
            <button onClick={() => navigate('/messages')} data-testid="open-messages-button" className="hg-press relative w-11 h-11 rounded-full bg-[#FAF6EC] hg-float flex items-center justify-center flex-shrink-0">
              <Mail className="w-[18px] h-[18px] text-[#1A4331]" />
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C66B4A] text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>}
            </button>
          )}
        </div>

        {!isPremium ? (
          <>
            {/* Vege Exchange is free for everyone — surface it even for non-members */}
            <HubTile image={HUB_IMG.market} icon={Repeat} title="Vege Growers Exchange" subtitle="Swap local seeds & produce" onClick={() => setSection(section === 'market' ? 'feed' : 'market')} testid="hub-market" />
            {section === 'market' ? (
              <Marketplace user={user} focusListingId={focusListing} />
            ) : (
              <>
                <GardenStream user={user} onUpgrade={goUpgrade} />
                <CommunityTeaser onUpgrade={goUpgrade} />
              </>
            )}
          </>
        ) : (
          <>
            {/* Navigation Ticker — frosted glass mini-cards, gold-glow active */}
            <div className="flex gap-3 overflow-x-auto pb-1 hg-scroll" data-testid="community-action-bar">
              {ACTIONS.map((a) => {
                const Icon = a.icon;
                const active = a.section && section === a.section;
                return (
                  <button key={a.id} onClick={() => pickAction(a)} data-testid={`action-${a.id}`}
                    className={`hg-glass hg-press flex-shrink-0 flex items-center gap-2 px-4 h-11 rounded-2xl transition-all ${active ? 'hg-action-active' : ''}`}
                    style={active ? { background: 'rgba(201,168,91,0.18)' } : undefined}>
                    <Icon className="w-4 h-4" style={{ color: active ? '#C9A85B' : '#1A4331' }} />
                    <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: active ? '#1A4331' : '#5C665C' }}>{a.label}</span>
                  </button>
                );
              })}
            </div>

            {/* ─────────── FEED ─────────── */}
            {section === 'feed' && (
              <div className="space-y-6">
                {/* Immersive hub tiles */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <HubTile image={HUB_IMG.market} icon={Repeat} title="Vege Growers Exchange" subtitle="Swap local seeds & produce" onClick={() => setSection('market')} testid="hub-market" />
                  <HubTile image={HUB_IMG.leaderboards} icon={Trophy} title="Leaderboard" subtitle="This week's heroes" onClick={() => setSection('leaderboards')} testid="hub-leaderboards" />
                </div>
                <HubTile wide image={HUB_IMG.qa} icon={MessageCircleQuestion} title="Ask the community for advice" subtitle="Growers near you can help — tap to ask" onClick={() => setSection('qa')} testid="hub-ask" badge={unseen} />

                {/* Living Garden Stream — collapsible */}
                <CollapsibleSection title="Living Garden Stream" subtitle="Fresh from gardens near you" icon={Leaf} persistKey="community_collapse_stream" testid="section-stream">
                  <GardenStream user={user} onUpgrade={goUpgrade} embedded />
                </CollapsibleSection>

                {/* Swap & Sell carousel — directly below the Living Garden Stream */}
                <CollapsibleSection title="Swap & Sell" subtitle="Recently listed produce, veg & seed" icon={Repeat} persistKey="community_collapse_swap" testid="section-swap">
                  <HomeSwapCarousel onOpenListing={openListing} onViewAll={() => { setFocusListing(null); setSection('market'); }} />
                </CollapsibleSection>

                {/* AI Local Digest — collapsible */}
                {digest && (
                  <CollapsibleSection title="Local insights" subtitle="This week in your region" icon={Sparkles} persistKey="community_collapse_digest" testid="section-digest">
                    <div className="bg-gradient-to-br from-[#1A4331]/8 to-[#FAF6EC] rounded-3xl p-6 hg-float" data-testid="ai-digest">
                      <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 bg-[#1A4331] rounded-full flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-[#C9A85B]" /></div>
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-xs font-bold tracking-wider uppercase text-[#1A4331]">{digest.summary}</p>
                          <ul className="mt-2 space-y-1">{(digest.highlights || []).map((h, i) => <li key={i} className="text-sm text-[#2A3528]">• {h}</li>)}</ul>
                          {digest.top_tip && (
                            <div className="mt-3 pt-3 border-t border-[#1A4331]/10">
                              <p className="text-xs font-bold tracking-wider uppercase text-[#C9A85B] mb-1">Top community tip</p>
                              <p className="text-sm text-[#2A3528] italic">&quot;{digest.top_tip}&quot;</p>
                              {digest.top_tip_attribution && <p className="text-xs text-[#7A6B57] mt-1">— {digest.top_tip_attribution}</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleSection>
                )}

                {/* Regional Pulse — collapsible */}
                {nearYou?.trending_crops?.length > 0 && (
                  <CollapsibleSection title={`Popular in ${nearYou.region}`} subtitle="What's growing near you right now" icon={MapPin} persistKey="community_collapse_pulse" testid="section-pulse">
                    <div className="hg-glass rounded-3xl p-6 hg-float" data-testid="regional-pulse">
                      <div className="flex flex-wrap gap-2">
                        {nearYou.trending_crops.map((c) => (
                          <span key={c.name} className="bg-white/70 text-[#1A4331] text-sm px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1.5">
                            <Sprout className="w-3.5 h-3.5 text-[#6B9F4A]" /> {c.name} <span className="text-xs text-[#7A6B57]">{c.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </CollapsibleSection>
                )}

                {/* Community feed — collapsible; post buttons live in the header */}
                <CollapsibleSection
                  title="Community feed" subtitle="Updates & harvests from growers" icon={Users} persistKey="community_collapse_feed" testid="section-feed"
                  headerRight={(
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setConversationOpen(true)} data-testid="start-conversation-button" className="hg-press inline-flex items-center gap-1.5 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] text-sm font-semibold px-4 py-2 rounded-full"><MessageCircle className="w-4 h-4" /> <span className="hidden sm:inline">Start a conversation</span></button>
                      <button onClick={() => setShareOpen(true)} data-testid="open-share-button" className="hg-press inline-flex items-center gap-1.5 bg-[#6B9F4A]/15 hover:bg-[#6B9F4A]/25 text-[#1A4331] text-sm font-semibold px-4 py-2 rounded-full"><Sprout className="w-4 h-4" /> <span className="hidden sm:inline">Share harvest</span></button>
                    </div>
                  )}
                >
                  <FilterBar
                    regions={regions} country={country} regionCode={regionCode} followingOnly={followingOnly}
                    activeCountry={activeCountry}
                    onAll={() => { setFollowingOnly(false); setCountry('global'); setRegionCode(''); }} onFollowing={() => { setFollowingOnly(true); }}
                    onCountry={pickCountry} onRegion={setRegionCode}
                  />
                  <div key={feedFilterKey} className="hg-slide-in mt-3 space-y-3">
                    {feed.filter((p) => !p.is_expert).length === 0 ? (
                      <div className="bg-[#FAF6EC] rounded-3xl p-12 text-center hg-float" data-testid="feed-empty">
                        <Users className="w-12 h-12 text-[#8EAA97] mx-auto mb-3" />
                        <p className="text-[#7A6B57]">{followingOnly ? 'Follow some growers to fill this feed.' : 'No posts here yet — try a wider region.'}</p>
                      </div>
                    ) : feed.map((p) => (
                      <FeedPost
                        key={p.id} post={p} pop={pops[p.id]}
                        onOpen={() => navigate(`/community/post/${p.id}`)}
                        onHelpful={() => toggleHelpful(p.id)}
                        onNudge={() => nudgePost(p.id)}
                        onDoubleLike={() => doubleLike(p)}
                        onProfile={() => p.author_public && p.author_id && setOpenProfile(p.author_id)}
                        onBlocked={() => removeAuthorPosts(p.author_id)}
                        onEdit={editPost}
                        onDelete={deletePost}
                      />
                    ))}
                  </div>
                </CollapsibleSection>
              </div>
            )}

            {section === 'qa' && <CommunityQA user={user} />}
            {section === 'market' && <Marketplace user={user} focusListingId={focusListing} />}

            {/* Leaderboards */}
            {section === 'leaderboards' && (
              <div className="space-y-4">
                <div className="flex gap-2 overflow-x-auto pb-1 hg-scroll">
                  {boardsList.map((b) => (
                    <button key={b.id} onClick={() => setActiveBoard(b.id)} data-testid={`board-tab-${b.id}`}
                      className={`hg-press text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap transition-all ${activeBoard === b.id ? 'bg-[#1A4331] text-[#FAF6EC]' : 'bg-[#FAF6EC] text-[#1A4331] hg-float'}`}>
                      {b.label}
                    </button>
                  ))}
                </div>
                {board && (
                  <div className="rounded-3xl p-6 hg-float" data-testid={`leaderboard-${activeBoard}`}
                    style={{ background: 'rgba(250,246,236,0.72)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.5)' }}>
                    <h3 className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{board.title}</h3>
                    {board.subtitle && <p className="text-sm text-[#7A6B57] mt-1 mb-4">{board.subtitle}</p>}

                    {/* Pinned "Your Rank" HUD card */}
                    {board.viewer && (
                      <div data-testid="leaderboard-your-rank" className="sticky top-2 z-10 rounded-3xl p-4 flex items-center gap-3 mb-4 hg-float"
                        style={{ background: 'linear-gradient(120deg, #1A4331, #255C44)' }}>
                        <div className="flex flex-col items-center justify-center rounded-2xl px-3 py-1.5 flex-shrink-0" style={{ background: 'rgba(180,211,48,0.18)' }}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: '#C9A85B' }}>Your rank</span>
                          <span className="text-2xl font-bold leading-none mt-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#FAF6EC' }}>{board.viewer.your_rank ? `#${board.viewer.your_rank}` : '—'}</span>
                        </div>
                        <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(180,211,48,0.25)' }}>
                          {board.viewer.avatar ? <img src={board.viewer.avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-[#FAF6EC] font-bold text-sm">{(board.viewer.display_name?.[0] || '?').toUpperCase()}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#FAF6EC] truncate">{board.viewer.display_name}</p>
                          <p className="text-xs truncate" style={{ color: 'rgba(250,246,236,0.75)' }}>
                            {board.viewer.your_rank ? (board.viewer.in_top ? 'On the podium 🏆' : 'Keep growing to climb') : 'Not ranked yet — share a harvest to join'}
                          </p>
                        </div>
                        <p className="text-lg font-bold whitespace-nowrap" style={{ color: '#B4D330' }}>
                          {board.viewer.unit === '$' ? '$' : ''}{board.viewer.value}{board.viewer.unit && board.viewer.unit !== '$' ? ` ${board.viewer.unit}` : ''}
                        </p>
                      </div>
                    )}

                    {(board.entries || []).length === 0 ? (
                      <p className="text-sm text-[#7A6B57] italic">No entries yet.</p>
                    ) : (
                      <>
                        <Podium entries={board.entries} />
                        <ol className="space-y-2 mt-2">
                          {board.entries.filter((e) => e.rank > 3).map((e) => (
                            <li key={e.rank} data-testid={e.is_viewer ? 'lb-row-viewer' : `lb-row-${e.rank}`}
                              className={`flex items-center gap-3 p-3 rounded-2xl ${e.is_viewer ? 'border' : ''}`}
                              style={e.is_viewer ? { background: 'rgba(180,211,48,0.18)', borderColor: 'rgba(26,67,49,0.18)' } : { background: 'rgba(255,255,255,0.5)' }}>
                              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={e.is_viewer ? { background: '#1A4331', color: '#FAF6EC' } : { background: '#E5EBDD', color: '#7A6B57' }}>{e.rank}</span>
                              <div className="w-9 h-9 rounded-full overflow-hidden bg-[#6B9F4A]/20 flex items-center justify-center flex-shrink-0">
                                {e.avatar ? <img src={e.avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-[#1A4331] font-bold text-sm">{(e.display_name?.[0] || '?').toUpperCase()}</span>}
                              </div>
                              <div className="flex-1 min-w-0"><p className="font-semibold text-[#1A4331] text-sm truncate">{e.display_name}{e.is_viewer ? ' · You' : ''}</p>{e.region && <p className="text-xs text-[#7A6B57]">{e.region}</p>}</div>
                              <p className="text-sm font-bold text-[#1A4331] whitespace-nowrap">{e.unit === '$' ? '$' : ''}{e.value}{e.unit && e.unit !== '$' ? ` ${e.unit}` : ''}</p>
                            </li>
                          ))}
                        </ol>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Challenges */}
            {section === 'challenges' && (
              <div className="space-y-3">
                {challenges.map((c) => {
                  const joined = myChallenges.find((mc) => mc.challenge_id === c.id);
                  return (
                    <div key={c.id} data-testid={`challenge-${c.id}`} className="bg-[#FAF6EC] rounded-3xl p-6 hg-float">
                      <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 bg-[#C9A85B]/20 rounded-full flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5 text-[#C9A85B]" /></div>
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            <span className="font-bold tracking-wider uppercase text-[#C9A85B]">+{c.reward_xp} XP</span>
                            <span className="text-[#7A6B57] capitalize">· {c.difficulty}</span>
                            {c.season !== 'any' && <span className="text-[#7A6B57] capitalize">· {c.season}</span>}
                          </div>
                          <h4 className="text-xl font-semibold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.title}</h4>
                          <p className="text-sm text-[#2A3528] mt-1">{c.description}</p>
                        </div>
                        {joined ? (
                          <span className="bg-[#6B9F4A]/20 text-[#4E7A33] font-semibold py-2 px-5 rounded-full text-sm inline-flex items-center gap-1.5"><UserCheck className="w-4 h-4" /> Joined</span>
                        ) : (
                          <button onClick={() => joinChallenge(c.id)} data-testid={`join-${c.id}`} className="hg-press bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-2 px-5 rounded-full text-sm transition-colors">Join challenge</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {openProfile && <GrowerProfileModal userId={openProfile} onClose={() => setOpenProfile(null)} />}
      {shareOpen && <ShareHarvestModal onClose={() => setShareOpen(false)} onShared={loadAll} user={user} />}
      {conversationOpen && <ConversationModal onClose={() => setConversationOpen(false)} onShared={() => { loadFeed(); }} regionLabel={nearYou?.region} />}
    </Layout>
  );
}

function HubTile({ image, icon: Icon, title, subtitle, onClick, wide, testid, badge }) {
  return (
    <button onClick={onClick} data-testid={testid} className="hg-press relative w-full overflow-hidden rounded-3xl text-left hg-float group" style={{ height: wide ? 128 : 168 }}>
      <img src={image} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,45,31,0.9), rgba(15,45,31,0.5) 45%, rgba(15,45,31,0.12))' }} />
      <div className="absolute top-4 left-4 w-9 h-9 rounded-full bg-[rgba(26,67,49,0.55)] backdrop-blur-sm flex items-center justify-center"><Icon className="w-4 h-4 text-[#FAF6EC]" /></div>
      {badge > 0 && (
        <div className="absolute top-4 right-4 bg-[#C66B4A] text-[#FAF6EC] text-[11px] font-bold px-2.5 py-1 rounded-full" data-testid={`${testid}-badge`}>{badge > 9 ? '9+' : badge} new</div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="text-[#FAF6EC] font-bold text-[15px]" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</p>
        <p className="text-[rgba(250,246,236,0.88)] text-xs mt-0.5">{subtitle}</p>
      </div>
    </button>
  );
}

function FilterBar({ regions, country, regionCode, followingOnly, activeCountry, onAll, onFollowing, onCountry, onRegion }) {
  const subRegions = activeCountry?.regions || [];
  return (
    <div className="bg-[#FAF6EC] rounded-3xl p-3 hg-float" data-testid="feed-filter-bar">
      <div className="flex items-center gap-2">
        <button onClick={onAll} data-testid="feed-filter-all" className={`hg-press text-xs font-semibold px-4 py-2 rounded-full transition-colors ${!followingOnly ? 'bg-[#1A4331] text-[#FAF6EC]' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}>All growers</button>
        <button onClick={onFollowing} data-testid="feed-filter-following" className={`hg-press flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${followingOnly ? 'bg-[#1A4331] text-[#FAF6EC]' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}><UserCheck className="w-3.5 h-3.5" /> Following</button>
      </div>
      {!followingOnly && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1 pt-3 hg-scroll">
            {(regions.countries || []).filter((c) => c.code !== 'global' && c.code !== 'au').map((c) => (
              <button key={c.code} onClick={() => onCountry(c.code)} data-testid={`region-country-${c.code}`}
                className={`hg-press flex-shrink-0 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${country === c.code ? 'bg-[#6B9F4A] text-white' : 'bg-white text-[#1A4331] hg-float'}`}>{c.label}</button>
            ))}
          </div>
          {subRegions.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 pt-2 hg-scroll" data-testid="region-subfilter">
              <button onClick={() => onRegion('')} data-testid="region-all" className={`hg-press flex-shrink-0 text-xs font-semibold px-4 py-1.5 rounded-full transition-colors ${!regionCode ? 'bg-[#C9A85B] text-[#1A4331]' : 'bg-[#6B9F4A]/12 text-[#7A6B57]'}`}>All regions</button>
              {subRegions.map((r) => (
                <button key={r.code} onClick={() => onRegion(r.code)} data-testid={`region-${r.code}`}
                  className={`hg-press flex-shrink-0 text-xs font-semibold px-4 py-1.5 rounded-full transition-colors ${regionCode === r.code ? 'bg-[#C9A85B] text-[#1A4331]' : 'bg-[#6B9F4A]/12 text-[#7A6B57]'}`}>{r.label}</button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FeedPost({ post: p, pop, onOpen, onHelpful, onNudge, onDoubleLike, onProfile, onBlocked, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editNote, setEditNote] = useState(p.note || '');
  const [savingEdit, setSavingEdit] = useState(false);
  const hasMedia = (p.photo_urls || []).length > 0 || !!p.video_url;
  const expert = p.is_expert;
  const mine = p.is_mine;
  return (
    <div data-testid={`feed-post-${p.id}`} className="relative bg-[#FAF6EC] rounded-3xl p-6 hg-float">
      <div className="flex items-start gap-3">
        <button onClick={onProfile} className="w-10 h-10 rounded-full bg-[#6B9F4A]/20 flex items-center justify-center text-[#1A4331] font-bold flex-shrink-0" data-testid={`feed-avatar-${p.id}`}>
          {expert ? <Star className="w-4 h-4 text-[#C9A85B]" fill="#C9A85B" /> : (p.display_name?.[0] || '?').toUpperCase()}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <button onClick={onProfile} data-testid={`feed-author-${p.id}`} className={`font-semibold text-[#1A4331] ${p.author_public && p.author_id ? 'hover:underline' : 'cursor-default'}`}>{p.display_name}</button>
            {(p.region_label || p.region) && <span className="text-[#7A6B57]">· {p.region_label || p.region}</span>}
            {!expert && (
              <span data-testid={`feed-kind-${p.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${p.post_type === 'note' ? 'bg-[#6B9F4A]/14 text-[#4E7A33]' : 'bg-[#C9A85B]/18 text-[#8A6D2B]'}`}>
                {p.post_type === 'note' ? <MessageCircle className="w-3 h-3" /> : <Sprout className="w-3 h-3" />}
                {p.post_type === 'note' ? 'UPDATE' : 'HARVEST'}
              </span>
            )}
            {p.growing_style && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${STYLE_COLORS[p.growing_style] || 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{p.growing_style}</span>}
          </div>
          <button onClick={editing ? undefined : onOpen} className="block text-left w-full">
            {(p.title || p.crop_name) && <h4 className="text-lg font-semibold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title || p.crop_name}{p.variety ? ` · ${p.variety}` : ''}</h4>}
            {(p.harvest_quantity || p.weight_kg) && (
              <p className="text-sm text-[#2A3528] mt-0.5">
                Harvested {p.harvest_quantity ? `${p.harvest_quantity}${p.harvest_unit === 'kg' ? 'kg' : ` ${p.harvest_unit}`}` : `${p.weight_kg}kg`}
                {p.estimated_value_low && p.estimated_value_high && <span className="text-[#C9A85B] font-semibold"> · ${p.estimated_value_low}–${p.estimated_value_high}</span>}
              </p>
            )}
            {!editing && p.note && (p.post_type === 'note'
              ? <p className="text-[15px] text-[#2A3528] mt-1.5 leading-relaxed whitespace-pre-wrap">{p.note}</p>
              : <p className="text-sm text-[#2A3528] mt-1.5 italic leading-relaxed">&quot;{p.note}&quot;</p>)}
          </button>
          {editing && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <textarea value={editNote} onChange={(e) => setEditNote(e.target.value)} rows={3} data-testid={`post-edit-input-${p.id}`}
                className="w-full px-3 py-2 bg-white rounded-xl border border-[#1A4331]/15 outline-none focus:border-[#1A4331] text-sm" />
              <div className="flex gap-2 mt-2">
                <button disabled={savingEdit} onClick={async () => { setSavingEdit(true); await onEdit(p.id, editNote); setSavingEdit(false); setEditing(false); }} data-testid={`post-edit-save-${p.id}`}
                  className="bg-[#1A4331] text-white text-xs font-semibold py-2 px-4 rounded-full disabled:opacity-60">{savingEdit ? 'Saving…' : 'Save'}</button>
                <button onClick={() => { setEditNote(p.note || ''); setEditing(false); }} className="text-xs font-semibold text-[#7A6B57] py-2 px-4 rounded-full border border-[#1A4331]/15">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {!expert && (
          <button onClick={() => setMenuOpen(true)} data-testid={`post-more-${p.id}`} className="hg-press w-8 h-8 rounded-full flex items-center justify-center text-[#7A6B57] hover:bg-black/5 flex-shrink-0">
            <MoreHorizontal className="w-5 h-5" />
          </button>
        )}
      </div>
      {p.is_starter && (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#4E7A33] bg-[#6B9F4A]/12 px-2 py-0.5 rounded-full mt-2" data-testid={`starter-badge-${p.id}`}>
          <Sprout className="w-3 h-3" /> From the founders
        </span>
      )}

      {hasMedia && (
        <div className="mt-4 relative rounded-2xl overflow-hidden" onDoubleClick={(e) => { e.stopPropagation(); onDoubleLike(); }} data-testid={`feed-media-${p.id}`}>
          <MediaGallery photoUrls={p.photo_urls} videoUrl={p.video_url} cropName={p.crop_name} postId={p.id} />
          {pop && (
            <div key={pop} className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
              <img src="/tomato-pop.png" alt="" className="w-32 h-32 hg-heart-pop drop-shadow-2xl" data-testid={`feed-pop-${p.id}`} />
            </div>
          )}
        </div>
      )}

      {!expert ? (
        <div className="flex items-center gap-6 mt-4 flex-wrap">
          <button onClick={onHelpful} data-testid={`helpful-${p.id}`} className={`hg-press flex items-center gap-1.5 text-xs font-semibold transition-colors ${p.viewer_helpful ? 'text-[#C66B4A]' : 'text-[#7A6B57] hover:text-[#C66B4A]'}`}>
            <Heart className={`w-4 h-4 ${p.viewer_helpful ? 'fill-[#C66B4A]' : ''}`} /> Helpful{p.helpful_count ? ` ${p.helpful_count}` : ''}
          </button>
          <button onClick={onNudge} data-testid={`feed-nudge-${p.id}`} className={`hg-press flex items-center gap-1.5 text-xs font-semibold transition-colors ${p.viewer_nudged ? 'text-[#C9A85B]' : 'text-[#7A6B57] hover:text-[#C9A85B]'}`}>
            <Hand className="w-4 h-4" /> Nudge{p.nudge_count ? ` ${p.nudge_count}` : ''}
          </button>
          <button onClick={onOpen} data-testid={`feed-comment-${p.id}`} className="hg-press flex items-center gap-1.5 text-xs font-semibold text-[#7A6B57] hover:text-[#1A4331] transition-colors">
            <MessageCircle className="w-4 h-4" /> {p.comment_count ? p.comment_count : 'Comment'}
          </button>
          <span className="text-xs text-[#7A6B57] ml-auto">{new Date(p.created_at).toLocaleDateString()}</span>
        </div>
      ) : (
        <div className="flex items-center mt-4">
          <span className="inline-flex items-center gap-1.5 bg-[#C9A85B]/20 text-[#8A6D2B] text-xs font-bold px-3 py-1 rounded-full"><Star className="w-3.5 h-3.5" /> Expert tip</span>
          <span className="text-xs text-[#7A6B57] ml-auto">{new Date(p.created_at).toLocaleDateString()}</span>
        </div>
      )}

      {menuOpen && (mine
        ? <OwnerPostMenu post={p} onClose={() => setMenuOpen(false)}
            onEdit={() => { setMenuOpen(false); setEditNote(p.note || ''); setEditing(true); }}
            onDelete={async () => { setMenuOpen(false); if (window.confirm('Remove this post? This can\'t be undone.')) await onDelete(p.id); }} />
        : <PostActionMenu post={p} onClose={() => setMenuOpen(false)} onBlocked={onBlocked} />
      )}
    </div>
  );
}

// Frosted-glass owner menu — Edit / Remove your own post (e.g. a founder starter post).
function OwnerPostMenu({ post: p, onClose, onEdit, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={`owner-menu-${p.id}`}>
      <div className="bg-[#FAF6EC] rounded-3xl p-2 w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
        <button onClick={onEdit} data-testid={`post-edit-${p.id}`} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-black/5 text-[#1A4331] font-semibold text-sm">
          <Pencil className="w-4 h-4" /> Edit post
        </button>
        <button onClick={onDelete} data-testid={`post-delete-${p.id}`} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-[#C66B4A]/10 text-[#C66B4A] font-semibold text-sm">
          <Trash2 className="w-4 h-4" /> Remove post
        </button>
        <button onClick={onClose} className="w-full px-4 py-3 rounded-2xl text-[#7A6B57] font-semibold text-sm">Cancel</button>
      </div>
    </div>
  );
}

// Frosted-glass Report / Block menu — matches the native action sheet.
function PostActionMenu({ post: p, onClose, onBlocked }) {
  const [done, setDone] = useState(null);
  const report = async () => {
    await axios.post(`${API}/community/report`, { target_type: 'post', target_id: p.id, reason: 'Reported from feed' }, { withCredentials: true }).catch(() => {});
    setDone('Thanks — our team will review this shortly.');
  };
  const block = async () => {
    if (!p.author_id) return;
    await axios.post(`${API}/community/block`, { user_id: p.author_id }, { withCredentials: true }).catch(() => {});
    onClose();
    onBlocked?.();
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0F2D1F]/45 flex items-center justify-center p-6" onClick={onClose} data-testid={`post-action-menu-${p.id}`}>
      <div className="w-full max-w-xs rounded-3xl overflow-hidden hg-zoom-in" onClick={(e) => e.stopPropagation()} style={{ background: 'rgba(247,244,236,0.82)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 30px 70px -20px rgba(15,45,31,0.55)' }}>
        <div className="p-5 space-y-3">
          {done ? (
            <p className="text-sm text-[#1A4331] text-center py-2">{done}</p>
          ) : (
            <>
              <p className="text-[15px] font-semibold text-[#1A4331] text-center">{p.display_name || 'This grower'}</p>
              <button onClick={report} data-testid={`report-post-${p.id}`} className="hg-press w-full min-h-[48px] flex items-center justify-center gap-2 rounded-full bg-[#FAF6EC] text-[#1A4331] text-sm font-semibold border border-[#1A4331]/12">
                <Flag className="w-4 h-4" /> Report post
              </button>
              {p.author_id && (
                <button onClick={block} data-testid={`block-grower-${p.id}`} className="hg-press w-full min-h-[48px] flex items-center justify-center gap-2 rounded-full bg-[#FAF6EC] text-[#C66B4A] text-sm font-semibold border border-[#C66B4A]/25">
                  <Ban className="w-4 h-4" /> Block grower
                </button>
              )}
            </>
          )}
          <button onClick={onClose} data-testid={`post-action-cancel-${p.id}`} className="hg-press w-full min-h-[44px] text-sm text-[#7A6B57]">{done ? 'Done' : 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}

function Podium({ entries }) {
  const top = entries.slice(0, 3);
  const order = [top[1], top[0], top[2]];
  const heights = [72, 100, 60];
  const tints = ['#8EAA97', '#C9A85B', '#C66B4A'];
  const fmt = (e) => `${e.unit === '$' ? '$' : ''}${e.value}${e.unit && e.unit !== '$' ? ` ${e.unit}` : ''}`;
  return (
    <div className="flex items-end justify-center gap-4 mt-2 mb-3" data-testid="leaderboard-podium">
      {order.map((e, i) => e ? (
        <div key={e.rank} className="flex-1 flex flex-col items-center">
          <div className={`relative rounded-full overflow-visible flex items-center justify-center bg-[#6B9F4A]/20 border-2 ${i === 1 ? 'w-16 h-16' : 'w-13 h-13'}`} style={{ width: i === 1 ? 64 : 52, height: i === 1 ? 64 : 52, borderColor: e.is_viewer ? '#B4D330' : (i === 1 ? '#C9A85B' : '#E5EBDD'), boxShadow: e.is_viewer ? '0 0 0 3px rgba(180,211,48,0.35)' : 'none' }}>
            {e.avatar ? <img src={e.avatar} alt="" className="w-full h-full object-cover rounded-full" /> : <span className="text-[#1A4331] font-bold">{(e.display_name?.[0] || '?').toUpperCase()}</span>}
            <span className="absolute -bottom-1.5 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-[#FAF6EC]" style={{ background: tints[i] }}>{e.rank}</span>
          </div>
          <p className="text-xs font-semibold text-[#1A4331] mt-2 max-w-[96px] truncate text-center">{e.is_viewer ? 'You' : e.display_name}</p>
          <p className="text-xs font-bold text-[#C9A85B]">{fmt(e)}</p>
          <div className="w-full rounded-t-lg mt-1.5 flex items-center justify-center" style={{ height: heights[i], background: tints[i] }}>
            {i === 1 && <Award className="w-5 h-5 text-[#FAF6EC]" />}
          </div>
        </div>
      ) : <div key={`empty-${i}`} className="flex-1" />)}
    </div>
  );
}

function ConversationModal({ onClose, onShared, regionLabel }) {
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/community/posts`, {
        post_type: 'note', note: note.trim(), photo_urls: photos, visibility: 'public',
      }, { withCredentials: true });
      onShared(); onClose();
    } catch (e) { /* noop */ } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto hg-zoom-in" onClick={(e) => e.stopPropagation()} data-testid="conversation-modal">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-8 h-8 rounded-full bg-[#6B9F4A]/15 flex items-center justify-center"><MessageCircle className="w-4 h-4 text-[#4E7A33]" /></span>
          <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Start a conversation</h3>
        </div>
        <p className="text-sm text-[#7A6B57] mb-4">Share something with the community — a win, a question for the group, or what you&apos;re growing this week.</p>
        <form onSubmit={submit} className="space-y-3">
          <textarea autoFocus required placeholder="What's happening in your garden?" value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={1000} data-testid="conversation-text"
            className="w-full px-4 py-3 bg-[#F4EEDE] rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] resize-none" />
          <MediaPicker photos={photos} setPhotos={setPhotos} video={null} setVideo={() => {}} maxPhotos={1} allowVideo={false} />
          {regionLabel && (
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A4331] bg-[#6B9F4A]/12 px-3 py-1.5 rounded-full" data-testid="conversation-region"><MapPin className="w-3.5 h-3.5 text-[#4E7A33]" /> Posting from {regionLabel}</p>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border-2 border-[#8EAA97] text-[#1A4331] py-3 rounded-full font-semibold">Cancel</button>
            <button type="submit" disabled={submitting || !note.trim()} data-testid="conversation-submit" className="flex-[1.5] bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50">{submitting ? 'Sharing…' : 'Share with the community'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShareHarvestModal({ onClose, onShared, user }) {
  const [form, setForm] = useState({
    crop_name: '', variety: '', harvest_quantity: '', harvest_unit: 'kg',
    estimated_value_low: '', estimated_value_high: '', note: '',
    growing_style: user?.growing_preference || 'organic', visibility: 'public', would_grow_again: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/community/posts`, {
        ...form,
        harvest_quantity: parseFloat(form.harvest_quantity) || null,
        estimated_value_low: parseFloat(form.estimated_value_low) || null,
        estimated_value_high: parseFloat(form.estimated_value_high) || null,
        photo_urls: photos, video_url: video,
      }, { withCredentials: true });
      onShared(); onClose();
    } catch (e) { /* noop */ } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto hg-zoom-in" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <h3 className="text-2xl font-bold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Share your harvest</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Crop name (e.g., Tomato)" value={form.crop_name} onChange={(e) => setForm({ ...form, crop_name: e.target.value })} data-testid="share-crop-input" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
          <input placeholder="Variety (optional)" value={form.variety} onChange={(e) => setForm({ ...form, variety: e.target.value })} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" step="0.1" placeholder="Quantity" value={form.harvest_quantity} onChange={(e) => setForm({ ...form, harvest_quantity: e.target.value })} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
            <select value={form.harvest_unit} onChange={(e) => setForm({ ...form, harvest_unit: e.target.value })} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]">
              <option value="kg">kg</option><option value="bunches">bunches</option><option value="heads">heads</option><option value="pieces">pieces</option>
            </select>
          </div>
          <textarea placeholder="A quick tip or note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
          <MediaPicker photos={photos} setPhotos={setPhotos} video={video} setVideo={setVideo} maxPhotos={5} />
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border-2 border-[#8EAA97] text-[#1A4331] py-3 rounded-full font-semibold">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="share-submit" className="flex-1 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50">{submitting ? 'Sharing…' : 'Share Harvest'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Community;
